import { z } from 'zod';
import { cinsiyetSchema, isoTarihSchema } from './identity.js';

/**
 * KBS (Kimlik Bildirim Sistemi) veri sozlesmesi.
 *
 * NOT: Resmi KBS web servisinin alan adlari/WSDL detaylari tesise verilen
 * kurum dokumaninda yer alir ve burada UYDURULMAZ. Bu sema, uygulamanin
 * kendi ic sozlesmesidir; adaptorler (bkz. apps/api/src/kbs/*) bu semayi
 * kurumun bekledigi yuke cevirmekle yukumludur.
 */

export const kalisAmaciSchema = z.enum([
  'TURIZM',
  'IS',
  'EGITIM',
  'SAGLIK',
  'AKRABA_ZIYARETI',
  'TRANSIT',
  'DIGER',
]);
export type KalisAmaci = z.infer<typeof kalisAmaciSchema>;

export const kbsDurumSchema = z.enum([
  'BEKLIYOR', // kuyruga alindi, henuz gonderilmedi
  'GONDERILIYOR',
  'GONDERILDI',
  'HATA',
  'SURESI_DOLMAK_UZERE', // yasal bildirim suresi doluyor, hala gonderilmedi
  'IPTAL',
]);
export type KbsDurum = z.infer<typeof kbsDurumSchema>;

export const kbsIslemTuruSchema = z.enum(['GIRIS', 'CIKIS', 'GUNCELLEME', 'IPTAL']);
export type KbsIslemTuru = z.infer<typeof kbsIslemTuruSchema>;

/** Bir misafirin KBS'ye bildirilecek kimlik gövdesi. */
export const kbsMisafirSchema = z.object({
  guestId: z.string(),
  tckn: z.string().nullable(),
  pasaportNo: z.string().nullable(),
  ad: z.string().min(1),
  soyad: z.string().min(1),
  anneAdi: z.string().nullable(),
  babaAdi: z.string().nullable(),
  dogumTarihi: isoTarihSchema,
  dogumYeri: z.string().nullable(),
  cinsiyet: cinsiyetSchema.exclude(['BILINMIYOR']),
  uyruk: z.string().length(3),
  belgeSeriNo: z.string().nullable(),
  kalisAmaci: kalisAmaciSchema.default('TURIZM'),
  /** 18 yas alti misafirin refakatcisi (ayni bildirimdeki baska bir guestId) */
  refakatciGuestId: z.string().nullable(),
});
export type KbsMisafir = z.infer<typeof kbsMisafirSchema>;

export const kbsBildirimSchema = z.object({
  tenantId: z.string(),
  reservationId: z.string(),
  islemTuru: kbsIslemTuruSchema,
  odaNo: z.string(),
  girisTarihi: z.string(), // ISO datetime
  cikisTarihi: z.string().nullable(),
  misafirler: z.array(kbsMisafirSchema).min(1),
});
export type KbsBildirim = z.infer<typeof kbsBildirimSchema>;

/** Adaptor cevabi. */
export const kbsSonucSchema = z.object({
  basarili: z.boolean(),
  /** Kurumdan donen referans/ takip numarasi */
  kurumReferansNo: z.string().nullable(),
  /** Ham cevap - audit log'a yazilir */
  hamCevap: z.string().nullable(),
  /**
   * KBS'nin KENDI kisa metni ("Yetkisiz IP", "Şifre Hatalı").
   *
   * `hataMesaji` bizim yazdigimiz yonlendirmeyi de icerdigi icin sebep
   * siniflandirmasi ondan YAPILAMAZ (bizim metnimiz de "IP" kelimesini
   * gecirir). Kurumun kendi sozleri ayri tutulur.
   */
  kurumMesaji: z.string().nullable(),
  hataKodu: z.string().nullable(),
  hataMesaji: z.string().nullable(),
  /** true ise tekrar denemek anlamsiz (veri hatasi), false ise gecici hata */
  kaliciHata: z.boolean(),
});
export type KbsSonuc = z.infer<typeof kbsSonucSchema>;

/** T.C. vatandasi misafir icin KBS'de zorunlu alanlar. */
export const KBS_ZORUNLU_TC = ['tckn', 'ad', 'soyad', 'dogumTarihi', 'anneAdi', 'babaAdi', 'cinsiyet'] as const;

/** Yabanci misafir icin KBS'de zorunlu alanlar. */
export const KBS_ZORUNLU_YABANCI = ['pasaportNo', 'ad', 'soyad', 'uyruk', 'dogumTarihi', 'cinsiyet'] as const;

/**
 * Yasal bildirim penceresi (saat). Tesis ayarindan degistirilebilir;
 * cron bu suenin %75'ine gelen kayitlari SURESI_DOLMAK_UZERE yapar.
 * TODO(kbs): Yururlukteki mevzuat suresini tesis ayarindan dogrula.
 */
export const KBS_BILDIRIM_PENCERESI_SAAT = 24;
